import { ref, reactive, computed } from 'vue';
import { BeiderMorse } from '../utils/BeiderMorse';
import { matchesAdvancedSearch } from '../utils/search';
import { normalizeThicknessName, slugifyFilterId } from '../utils/filterIds';

/** Stone types pushed to the end of the list when `stoneTypesLast` is enabled. */
export const DEFAULT_STONE_TYPES_LAST = ['Porcelain', 'Porcelain Slab', 'Quartz', 'Silestone', 'Precious Stone'];

export function useSlabFilters(itemsRef, counts, urlHelpers) {
    const { stockCount, availableCount } = counts;
    const { saveStateToUrl } = urlHelpers || { saveStateToUrl: () => { } };

    // --- State ---
    const search = ref("");
    const filters = ref({});
    const filterKeys = ref([]);
    const filteredItems = ref([]);
    const selectedOrder = ref('a-z');
    const loadedImages = ref({});

    // Toggles
    const dealerOnly = ref(false);
    const showDealerOnTop = ref(false);
    /** Stone type names (case-insensitive) grouped after all other items, keeping the chosen sort within each group. */
    const stoneTypesLast = ref([]);
    const onlyDealerItems = ref(false);
    const onlyDomesticItems = ref(false);
    const onlySignatureItems = ref(false);
    const onlyNewArrivals = ref(false);
    const onlyStockItems = ref(false);
    const onlySales = ref(false);
    const onlySingles = ref(false);
    /** When true with onlySingles, keep total_slabs === 1 (exclude doubles). */
    const singlesExact = ref(false);
    /** When true, keep only products with exactly two slabs. */
    const onlyDoubles = ref(false);
    const useSaleFilter = ref(false);
    const includeHolds = ref(false);

    // Dimensions
    const minWidth = ref(0);
    const maxWidth = ref(150);
    const minHeight = ref(0);
    const maxHeight = ref(100);

    /**
     * Filters shown inside another one, as { child: parent } (e.g. { fill: 'finish' }).
     * A child appears only once a parent value that has it is picked.
     */
    const dependentFilters = ref({});
    /** child → Map(parent id → Set(child ids)) seen together on the same items. */
    const dependentPairs = ref({});

    // UI Expansion State
    const openFilters = ref(['format', 'decorative_materials']);
    const openMobileFilters = ref(false);

    // Auto-expanding activeFilters: any new key accessed gets an empty array
    const activeFilters = reactive(new Proxy({}, {
        get(target, prop) {
            if (typeof prop === 'string' && !(prop in target)) {
                target[prop] = [];
            }
            return target[prop];
        }
    }));

    // Computed Helpers
    const flattenedActiveFilters = computed(() => {
        const flattened = [];
        Object.entries(activeFilters).forEach(([categoryKey, filters]) => {
            filters.forEach(filter => {
                flattened.push({
                    category: categoryKey,
                    value: filter
                });
            });
        });
        return flattened;
    });

    // Known title overrides; unknown categories auto-generate from key
    const knownTitles = {
        stoneType: 'Material',
        format: 'Format',
        color: 'Color',
        application: 'Application',
        material: 'Material',
        finish: 'Finish',
        brand: 'Brand',
        decorative_materials: 'Material',
        surface_category: 'Type of Texture',
        surface_indoor_application: 'Indoor Application',
        surface_application: 'Outdoor Application',
        location: 'Location',
        thickness: 'Thickness',
    };

    // Auto-generate title for unknown keys: snake_case/camelCase → Title Case
    const getFilterTitle = (key) => {
        if (knownTitles[key]) return knownTitles[key];
        return key
            .replace(/([a-z])([A-Z])/g, '$1 $2')  // camelCase split
            .replace(/[_-]/g, ' ')                  // snake/kebab split
            .replace(/\b\w/g, c => c.toUpperCase()); // capitalize words
    };

    // Proxy so templates can do filterTitles[key] and get auto-generated titles
    const filterTitles = new Proxy(knownTitles, {
        get(target, prop) {
            if (typeof prop === 'string') return getFilterTitle(prop);
            return target[prop];
        }
    });

    // --- Logic ---

    const decodeFilterLabel = (value) => String(value ?? '')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'");

    /**
     * Stable filter identity for matching/URLs.
     * Prefer human labels over numeric WordPress term IDs so active filters
     * stored as names (legacy WP templates) still match.
     */
    const getFilterItemId = (category, item) => {
        if (!item) return '';

        let value;
        if (category === 'thickness') {
            value = normalizeThicknessName(item.name || item.id);
        } else {
            const rawId = item.id;
            const isNumericId = rawId != null && rawId !== '' && /^\d+$/.test(String(rawId));
            value = isNumericId ? (item.name || rawId) : (rawId || item.name);
        }

        return slugifyFilterId(decodeFilterLabel(value));
    };

    // [Insert checkProductMatch and buildFilters here]
    // I will inline them to save specific tool calls, they were massive.

    /**
     * Checks if a specific product item matches the current filters and search criteria.
     */
    const checkProductMatch = (obj, context) => {
        if (!obj || typeof obj !== 'object' || !obj.name) return false;

        const {
            currentActiveFilters,
            hasSearch,
            isLotSearch,
            lotNumber,
            searchTerm,
            bm,
            stockVal,
            availableVal
        } = context;

        // 1. Toggles
        const onlyDealer = onlyDealerItems.value && !dealerOnly.value;

        if (onlyDealer && !obj.dealer_product) return false;
        if (onlySignatureItems.value && !obj.signature_product) return false;
        if (onlyNewArrivals.value && (!obj.tags || !obj.tags.includes("new"))) return false;
        if (onlyDomesticItems.value && obj.stock_status !== "stocked_domestically") return false;


        if (onlyDoubles.value) {
            if (obj.total_slabs === "") return false;
            if (isNaN(parseInt(obj.total_slabs))) return false;
            if (parseInt(obj.total_slabs) !== 2) return false;
        } else if (onlySingles.value) {
            if (obj.total_slabs === "") return false;
            if (isNaN(parseInt(obj.total_slabs))) return false;
            // Default: single/double (≤2). With singlesExact: singles only (≤1).
            const maxSlabs = singlesExact.value ? 1 : 2;
            if (parseInt(obj.total_slabs) > maxSlabs) return false;
        }

        // Slab Counts — includeHolds toggle (new UI) or dual inputs (old UI)
        let slabCt = parseInt(obj.slabs);
        if (isNaN(slabCt)) slabCt = 1;
        let totalSlabs = parseInt(obj.total_slabs);
        if (isNaN(totalSlabs)) totalSlabs = 1;

        if (includeHolds.value) {
            // New UI: single "Min Slabs" input checked against total_slabs
            if (totalSlabs < availableVal) return false;
        } else {
            // Old UI: stockCount filters total_slabs, availableCount filters slabs
            if (totalSlabs < stockVal) return false;
            if (slabCt < availableVal) return false;
        }

        if (onlySales.value) {
            if (!obj.tags || !obj.tags.includes("on_sale")) return false;
        }

        if (onlyStockItems.value && !obj.stock_product) return false;

        // 2. Dimensions
        const averageSize = obj.average_size;
        if (typeof averageSize === "string" && averageSize.includes("x")) {
            const sizeParts = averageSize.replace(/"/g, '').split("x");
            if (sizeParts.length === 2) {
                const w = parseFloat(sizeParts[0]);
                const h = parseFloat(sizeParts[1]);
                if (w < minWidth.value || h < minHeight.value || w > maxWidth.value || h > maxHeight.value) {
                    return false;
                }
            }
        }

        // 3. Categories
        const activeMatches = Object.entries(currentActiveFilters).every(([category, activeValues]) => {
            if (!obj.category || typeof obj.category !== 'object') return false;

            const categoryData = obj.category[category];
            const objectValues = [];

            if (Array.isArray(categoryData)) {
                categoryData.forEach(value => {
                    if (value?.id || value?.name) {
                        objectValues.push(getFilterItemId(category, value));
                    }
                });
            } else if (categoryData && categoryData.name) {
                objectValues.push(getFilterItemId(category, categoryData));
            }

            if (category === "color" && obj.category.additional_colors?.length) {
                obj.category.additional_colors.forEach(color => {
                    objectValues.push(getFilterItemId('color', color));
                });
            }

            return activeValues.some(active => objectValues.includes(getFilterItemId(category, { name: active })));
        });

        if (!activeMatches) return false;

        // 4. Search
        if (hasSearch) {
            if (isLotSearch && lotNumber) {
                return obj.lot != null && String(obj.lot).toLowerCase().startsWith(lotNumber.toLowerCase());
            } else {
                return matchesAdvancedSearch(searchTerm, obj.name, obj.alt_name, bm);
            }
        }

        return true;
    };

    const buildFilters = (items) => {
        // Discover all category keys dynamically from the data
        const discoveredCategories = new Set();
        items.forEach(item => {
            if (!item.category || typeof item.category !== 'object') return;
            Object.keys(item.category).forEach(key => {
                // Skip helper keys that aren't real filter categories
                if (key === 'additional_colors') return;
                discoveredCategories.add(key);
            });
        });

        const filterCategories = Array.from(discoveredCategories);
        const newFilters = Object.fromEntries(filterCategories.map(category => [category, []]));

        // Ensure activeFilters has an entry for every discovered category
        filterCategories.forEach(category => {
            if (!Array.isArray(activeFilters[category])) {
                activeFilters[category] = [];
            }
        });

        const normalizeFilterItem = (category, item) => {
            if (!item || !item.name) return;
            item.name = decodeFilterLabel(item.name);
            if (category === "thickness") {
                item.name = normalizeThicknessName(item.name);
            } else {
                item.name = item.name.replace("/", " / ");
            }
            if (category === "finish") {
                if (item.name === "Polished And Honed") item.name = "Honed & Polished";
                if (item.name === "Polished Or Glossy") {
                    item.name = "Polished";
                    item.id = "Polished";
                    item.meta = { textured: false };
                }
            }
            item.id = getFilterItemId(category, item);
        };

        items.forEach(item => {
            if (!item.category) return;
            filterCategories.forEach(category => {
                const categoryData = item.category[category];
                if (categoryData) {
                    const dataToAdd = Array.isArray(categoryData) ? categoryData : [categoryData];
                    dataToAdd.forEach(filterItem => {
                        normalizeFilterItem(category, filterItem);
                        if (filterItem.id) newFilters[category].push(filterItem);
                    });
                }
                if (category === "color" && item.category.additional_colors?.length) {
                    item.category.additional_colors.forEach(col => {
                        normalizeFilterItem(category, col);
                        if (col.id) {
                            newFilters.color.push(col);
                        }
                    });
                }
            });
        });

        const priorityOrderMap = { 'Polished': 1, 'Honed': 2, 'Satin': 3, 'Glossy': 4 };
        const sortFinishFn = (a, b) => {
            const isAPriority = priorityOrderMap.hasOwnProperty(a.name);
            const isBPriority = priorityOrderMap.hasOwnProperty(b.name);

            if (isAPriority && isBPriority) return priorityOrderMap[a.name] - priorityOrderMap[b.name];
            if (isAPriority) return -1;
            if (isBPriority) return 1;

            const aTextured = a.meta?.textured === true;
            const bTextured = b.meta?.textured === true;

            if (aTextured !== bTextured) return aTextured ? 1 : -1;

            return a.name.localeCompare(b.name);
        };
        const sortDefaultFn = (a, b) => (a.name || '').localeCompare(b.name || '');

        const sortedFilters = {};
        for (const [key, values] of Object.entries(newFilters)) {
            const uniqueMap = new Map();
            values.forEach(item => {
                const existing = uniqueMap.get(item.id);
                if (!existing || (item.meta && !existing.meta)) uniqueMap.set(item.id, item);
            });
            const uniqueValues = Array.from(uniqueMap.values());
            if (key === 'finish') uniqueValues.sort(sortFinishFn);
            else uniqueValues.sort(sortDefaultFn);
            sortedFilters[key] = uniqueValues;
        }
        buildDependentPairs(items);
        return sortedFilters;
    };

    /** Runs after buildFilters has normalised the item labels, so ids match the options. */
    const buildDependentPairs = (items) => {
        const pairsByChild = {};
        Object.entries(dependentFilters.value).forEach(([child, parent]) => {
            const pairs = new Map();
            items.forEach(item => {
                const childValues = [].concat(item.category?.[child] || []);
                [].concat(item.category?.[parent] || []).forEach(parentValue => {
                    const parentId = getFilterItemId(parent, parentValue);
                    if (!parentId) return;
                    childValues.forEach(childValue => {
                        const childId = getFilterItemId(child, childValue);
                        if (!childId) return;
                        if (!pairs.has(parentId)) pairs.set(parentId, new Set());
                        pairs.get(parentId).add(childId);
                    });
                });
            });
            pairsByChild[child] = pairs;
        });
        dependentPairs.value = pairsByChild;
    };

    const isDependentFilter = (key) => Object.prototype.hasOwnProperty.call(dependentFilters.value, key);

    const dependentFiltersOf = (parent) => Object.keys(dependentFilters.value)
        .filter(child => dependentFilters.value[child] === parent);

    /** Child ids that go with the parent values picked so far. */
    const dependentOptionIds = (child) => {
        const parent = dependentFilters.value[child];
        const pairs = dependentPairs.value[child];
        const ids = new Set();
        if (!parent || !pairs) return ids;

        activeFilters[parent].forEach(active => {
            pairs.get(getFilterItemId(parent, { name: active }))?.forEach(id => ids.add(id));
        });
        return ids;
    };

    /** The child's options to show under its parent; empty while no parent value has any. */
    const dependentFilterOptions = (child) => {
        const ids = dependentOptionIds(child);
        if (!ids.size) return [];
        return (filters.value[child] || []).filter(option => ids.has(option.id));
    };

    /** Drop child selections whose parent value was unpicked, so no hidden filter stays applied. */
    const pruneDependentFilters = () => {
        Object.keys(dependentFilters.value).forEach(child => {
            const active = activeFilters[child];
            if (!active.length) return;
            const ids = dependentOptionIds(child);
            const kept = active.filter(value => ids.has(getFilterItemId(child, { name: value })));
            if (kept.length !== active.length) activeFilters[child] = kept;
        });
    };

    const updateFilters = (settings = {}) => {
        pruneDependentFilters();

        // Auto-detect sale
        if (settings.saleFilter || useSaleFilter.value) {
            useSaleFilter.value = itemsRef.value.some(item => item.tags && item.tags.includes("on_sale"));
        }

        const bm = new BeiderMorse();
        // Ignore keys that are not real filter categories (e.g. utm_source or
        // gclid pushed in by embedding pages), otherwise nothing can match.
        const knownFilterKeys = new Set(filterKeys.value);
        const currentActiveFilters = Object.fromEntries(
            Object.entries(activeFilters).filter(([key, value]) =>
                value.length > 0 && (knownFilterKeys.size === 0 || knownFilterKeys.has(key))
            )
        );

        const thicknessMatch = search.value.match(/(\d+(?:\.\d+)?)\s*(cm|mm)/i);
        const thickness = thicknessMatch ? normalizeThicknessName(thicknessMatch[0]) : null;
        let searchTerm = search.value.replace(/(\d+(?:\.\d+)?)\s*(cm|mm)/i, "").trim();
        const hasSearch = searchTerm.length >= 3;
        const isLotSearch = searchTerm.toLowerCase().startsWith('lot:');
        const lotNumber = isLotSearch ? searchTerm.substring(4).trim() : null;

        const context = {
            currentActiveFilters,
            hasSearch,
            isLotSearch,
            lotNumber,
            searchTerm,
            bm,
            stockVal: stockCount.value,
            availableVal: availableCount.value
        };

        // Filter
        let results = itemsRef.value.filter(item => checkProductMatch(item, context));

        // Sort
        switch (selectedOrder.value) {
            case 'a-z': results.sort((a, b) => a.name.localeCompare(b.name)); break;
            case 'z-a': results.sort((a, b) => b.name.localeCompare(a.name)); break;
            case 'newest':
                results.sort((a, b) => {
                    const aReceivedAt = Date.parse(a.received_date);
                    const bReceivedAt = Date.parse(b.received_date);
                    const aHasReceivedDate = !Number.isNaN(aReceivedAt);
                    const bHasReceivedDate = !Number.isNaN(bReceivedAt);

                    if (aHasReceivedDate !== bHasReceivedDate) {
                        return aHasReceivedDate ? -1 : 1;
                    }

                    if (aHasReceivedDate && aReceivedAt !== bReceivedAt) {
                        return bReceivedAt - aReceivedAt;
                    }

                    return a.name.localeCompare(b.name);
                });
                break;
            case 'relevance': results.sort((a, b) => (a.match_order ?? 9999) - (b.match_order ?? 9999)); break;
        }

        // Post-Sort
        if (stoneTypesLast.value.length && selectedOrder.value !== 'relevance') {
            const lastTypes = new Set(stoneTypesLast.value.map(type => String(type).trim().toLowerCase()));
            const isLastType = (item) => lastTypes.has(String(item.category?.stoneType?.name ?? '').trim().toLowerCase());
            results = results.filter(item => !isLastType(item)).concat(results.filter(isLastType));
        }

        if (showDealerOnTop.value) {
            const dealer = [], other = [];
            results.forEach(i => i.dealer_product ? dealer.push(i) : other.push(i));
            results = dealer.concat(other);
        }

        if (thickness) {
            const thicknessId = getFilterItemId('thickness', { name: thickness });
            results = results.filter(item => {
                const itemThickness = item.category?.thickness;

                return itemThickness && getFilterItemId('thickness', itemThickness) === thicknessId;
            });
        }

        filteredItems.value = results;

        // Gather toggles for sync
        const toggles = {
            onlyNewArrivals, onlySales, onlySingles, singlesExact, onlyDoubles, onlyDealerItems, onlySignatureItems, onlyStockItems, onlyDomesticItems, includeHolds
        };
        const dimensions = { minWidth, maxWidth, minHeight, maxHeight };
        const counts = { stockCount, availableCount };
        saveStateToUrl({ search, activeFilters, toggles, dimensions, counts, order: selectedOrder });
    };

    const clearFilters = () => {
        Object.keys(activeFilters).forEach(key => activeFilters[key] = []);
        search.value = '';
        onlyNewArrivals.value = false;
        onlySales.value = false;
        onlySingles.value = false;
        singlesExact.value = false;
        onlyDoubles.value = false;
        onlyDealerItems.value = false;
        onlySignatureItems.value = false;
        onlyStockItems.value = false;
        onlyDomesticItems.value = false;
        includeHolds.value = false;
        minWidth.value = 0;
        maxWidth.value = 150;
        minHeight.value = 0;
        maxHeight.value = 100;
        stockCount.value = 0;
        availableCount.value = 0;
        selectedOrder.value = 'a-z';
        updateFilters();
    };

    const toggleFilter = (filter, id) => {
        if (activeFilters[filter].includes(id)) {
            activeFilters[filter] = activeFilters[filter].filter(item => item !== id);
        } else {
            activeFilters[filter].push(id);
        }
        updateFilters();
    };

    const toggleNewArrivals = () => {
        onlyNewArrivals.value = !onlyNewArrivals.value;
        updateFilters();
    };

    const toggleSaleFilter = () => {
        onlySales.value = !onlySales.value;
        updateFilters();
    };

    /**
     * Toggle singles filter.
     * @param {boolean} [exactOnly=false] When true, filter to single slabs only (total_slabs ≤ 1).
     *   When omitted/false, keep legacy single/double behavior (total_slabs ≤ 2).
     */
    const toggleSingleFilter = (exactOnly = false) => {
        const exact = exactOnly === true;

        if (onlySingles.value && singlesExact.value === exact) {
            onlySingles.value = false;
            singlesExact.value = false;
        } else {
            onlySingles.value = true;
            singlesExact.value = exact;
            onlyDoubles.value = false;
        }

        updateFilters();
    };

    const toggleDoubleFilter = () => {
        onlyDoubles.value = !onlyDoubles.value;
        if (onlyDoubles.value) {
            onlySingles.value = false;
            singlesExact.value = false;
        }

        updateFilters();
    };

    const toggleIncludeHolds = () => {
        includeHolds.value = !includeHolds.value;
        updateFilters();
    };

    const toggleTitle = (filter) => {
        if (openFilters.value.includes(filter)) {
            openFilters.value = openFilters.value.filter(item => item !== filter);
        } else {
            openFilters.value.push(filter);
        }
    };

    const toggleMobileFilter = () => {
        openMobileFilters.value = !openMobileFilters.value;
    };

    const toggleDealers = () => {
        onlyDealerItems.value = !onlyDealerItems.value;
        updateFilters();
    };

    const toggleDomestically = () => {
        onlyDomesticItems.value = !onlyDomesticItems.value;
        updateFilters();
    };

    const toggleSignature = () => {
        onlySignatureItems.value = !onlySignatureItems.value;
        updateFilters();
    };

    const toggleStock = () => {
        onlyStockItems.value = !onlyStockItems.value;
        updateFilters();
    };

    return {
        // State
        search, filters, filterKeys, filteredItems, activeFilters, flattenedActiveFilters,
        selectedOrder, loadedImages,
        dealerOnly, showDealerOnTop, stoneTypesLast, dependentFilters, onlyDealerItems, onlyDomesticItems, onlySignatureItems,
        onlyNewArrivals, onlyStockItems, onlySales, onlySingles, singlesExact, onlyDoubles, useSaleFilter, includeHolds,
        minWidth, maxWidth, minHeight, maxHeight,
        openFilters, openMobileFilters,
        filterTitles,

        // Methods
        updateFilters, buildFilters, clearFilters, toggleFilter,
        isDependentFilter, dependentFiltersOf, dependentFilterOptions,
        toggleNewArrivals, toggleSaleFilter, toggleSingleFilter, toggleDoubleFilter, toggleIncludeHolds,
        toggleTitle, toggleMobileFilter, toggleDealers, toggleDomestically,
        toggleSignature, toggleStock
    };
}
